import React, { useEffect, useMemo, useState } from "react";
// import { Dialog, Transition } from "@headlessui/react";
import { Fragment } from "react";
import {
  FaChevronDown,
  FaChevronRight,
  FaHashtag,
  FaTimes,
  FaHistory,
  FaBuilding,
  FaBriefcase,
  FaUserCircle
} from "react-icons/fa";
import { FaRegCircleCheck } from "react-icons/fa6";
import moment from "moment";
import { useDispatch, useSelector } from "react-redux";
import { fetchAllTaskReports } from "../../Redux/Slice/Day";
import defaultProfile from "../../Image/Daynamic_Logo_Piture.png";
import { useNavigate, useSearchParams } from "react-router-dom";
import Loader from "../../Components/Loader/Loader";
import { Autocomplete, TextField } from "@mui/material";

/* 🔹 Responsive max calls logic */
const getMaxCalls = () => {
  if (window.innerWidth < 640) return 4; // mobile
  if (window.innerWidth < 1024) return 6; // tablet
  return 8; // desktop
};

const TaskReport = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const [searchParams, setSearchParams] = useSearchParams();
  const [selectedCompany, setSelectedCompany] = useState(null);
  const [selectedMember, setSelectedMember] = useState(null);
  const [selectedProject, setSelectedProject] = useState(null);
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const { AllTaskReportData, AllTaskReportsLoading } = useSelector((state) => state.Day);

  const [open, setOpen] = useState(false);
  const [selectedUserId, setSelectedUserId] = useState(null);
  const [maxCalls, setMaxCalls] = useState(getMaxCalls());
  const [expandedUsers, setExpandedUsers] = useState({}); // Track which users have expanded calls

  useEffect(() => {
    dispatch(fetchAllTaskReports());
  }, [dispatch]);

  /* 🔹 Handle screen resize */
  useEffect(() => {
    const handleResize = () => setMaxCalls(getMaxCalls());
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  /* 🔥 Restore drawer from URL (BACK / REFRESH SAFE) */
  useEffect(() => {
    const drawer = searchParams.get("drawer");
    const userId = searchParams.get("userId");

    if (drawer === "open" && userId) {
      setSelectedUserId(userId);
      setOpen(true);
    }
  }, [searchParams]);

  /* ================= VALID USERS ================= */
  const validUsers =
    AllTaskReportData?.data?.filter(
      (u) => (u.Role || "").toLowerCase() !== "owner",
    ) || [];

  /* ================= DATE FILTER LOGIC ================= */
  const isDateInRange = (dateString) => {
    if (!startDate && !endDate) return true; // No date filter applied

    const taskDate = new Date(dateString);
    taskDate.setHours(0, 0, 0, 0);

    let start = null;
    let end = null;

    if (startDate) {
      start = new Date(startDate);
      start.setHours(0, 0, 0, 0);
    }

    if (endDate) {
      end = new Date(endDate);
      end.setHours(23, 59, 59, 999);
    }

    if (start && end) {
      return taskDate >= start && taskDate <= end;
    } else if (start) {
      return taskDate >= start;
    } else if (end) {
      return taskDate <= end;
    }

    return true;
  };

  /* ================= UNIQUE COMPANY LIST ================= */
  const companyOptions = useMemo(() => {
    const map = new Map();

    validUsers?.forEach((user) => {
      user?.RatingRecord?.forEach((r) => {
        if (isDateInRange(r.Date)) {
          const company = r?.ConplainId?.CompanyName;
          if (company?._id && company?.WhaClientName) {
            map.set(company._id, {
              id: company._id,
              name: company.WhaClientName,
            });
          }
        }
      });
    });

    return Array.from(map.values()).sort((a, b) => (a.name || "").localeCompare(b.name || ""));
  }, [validUsers, startDate, endDate]);

  /* ================= UNIQUE MEMBER LIST ================= */
  const memberOptions = useMemo(() => {
    const map = new Map();
    validUsers?.forEach((user) => {
      // Check if user has tasks in date range
      const hasTasksInRange = user?.RatingRecord?.some((r) =>
        isDateInRange(r.Date),
      );
      if (hasTasksInRange) {
        map?.set(user._id, { id: user._id, name: user.Name });
      }
    });
    return Array?.from(map?.values()).sort((a, b) => (a.name || "").localeCompare(b.name || ""));
  }, [validUsers, startDate, endDate]);

  /* ================= UNIQUE PROJECT LIST ================= */
  const projectOptions = useMemo(() => {
    const map = new Map();
    validUsers?.forEach((user) => {
      user?.RatingRecord?.forEach((r) => {
        if (isDateInRange(r.Date)) {
          const project = r?.ConplainId?.Project;
          if (project?._id && project?.project_name) {
            map.set(project._id, {
              id: project._id,
              name: project.project_name,
            });
          }
        }
      });
    });
    return Array.from(map.values()).sort((a, b) => (a.name || "").localeCompare(b.name || ""));
  }, [validUsers, startDate, endDate]);

  /* ================= FILTER USERS BY COMPANY + MEMBER + PROJECT + DATE ================= */
  const filteredUsers = useMemo(() => {
    return (
      validUsers
        .map((user) => {
          // Filter tasks by Company + Project + Date
          let filteredTasks =
            user?.RatingRecord?.filter((r) => {
              // Date filter
              if (!isDateInRange(r.Date)) return false;

              // Company filter
              if (
                selectedCompany &&
                r?.ConplainId?.CompanyName?._id !== selectedCompany.id
              )
                return false;

              // Project filter
              if (
                selectedProject &&
                r?.ConplainId?.Project?._id !== selectedProject.id
              )
                return false;

              return true;
            }) || [];

          return {
            ...user,
            RatingRecord: filteredTasks,
            taskCount: filteredTasks.length,
          };
        })
        // Don't remove users even if they have 0 tasks
        .filter((user) => {
          // Keep user if no member filter or member matches
          if (selectedMember) return user._id === selectedMember.id;
          return true;
        })
        // Sort: If any filter is applied, sort by task count (descending), otherwise alphabetical
        .sort((a, b) => {
          // Check if any filter is active
          const isFilterActive =
            selectedCompany ||
            selectedMember ||
            selectedProject ||
            startDate ||
            endDate;

          if (isFilterActive) {
            // Sort by task count descending (most tasks first)
            return b.taskCount - a.taskCount;
          } else {
            // Default alphabetical sort
            return (a.Name || "").localeCompare(b.Name || "");
          }
        })
    );
  }, [
    validUsers,
    selectedCompany,
    selectedMember,
    selectedProject,
    startDate,
    endDate,
  ]);

  /* ================= SELECTED USER ================= */
  const selectedUser = useMemo(() => {
    if (!selectedUserId) return null;
    return filteredUsers.find((u) => u._id === selectedUserId) || null;
  }, [filteredUsers, selectedUserId]);

  /* 🔹 Toggle expanded state for calls */
  const toggleExpanded = (userId) => {
    setExpandedUsers((prev) => ({
      ...prev,
      [userId]: !prev[userId],
    }));
  };

  /* 🔹 Get visible calls based on expanded state */
  const getVisibleCalls = (calls, userId) => {
    const isExpanded = expandedUsers[userId];
    if (isExpanded) return calls;
    return calls.slice(0, maxCalls);
  };

  /* 🔹 Close drawer */
  const closeDrawer = () => {
    setOpen(false);
    setSelectedUserId(null);
    setSearchParams({});
  };

  /* 🔹 Clear all filters */
  const clearAllFilters = () => {
    setSelectedCompany(null);
    setSelectedMember(null);
    setSelectedProject(null);
    setStartDate("");
    setEndDate("");
  };

  /* 🔹 Check if any filter is active */
  const isAnyFilterActive = useMemo(() => {
    return (
      selectedCompany ||
      selectedMember ||
      selectedProject ||
      startDate ||
      endDate
    );
  }, [selectedCompany, selectedMember, selectedProject, startDate, endDate]);

  if (AllTaskReportsLoading || !AllTaskReportData) return <Loader />;

  return (
    <>
      {/* Title */}
      <h3 className="text-xl mb-3 font-bold main_title_page text-white px-4 py-2 inline-block rounded">
        EMP Task Report
      </h3>
      <div className="min-h-screen bg-gray-100 ">
        {/* FILTER */}

        <div className="grid grid-cols-8 gap-3 Form_bg_color bg-white shadow-sm p-4 rounded-2xl mb-5">
          <div className="col-span-12 flex items-center justify-between gap-2 mb-2">
            <h2 className="text-lg font-semibold text-start">Filters :</h2>
            {isAnyFilterActive && (
              <button
                onClick={clearAllFilters}
                className="text-sm font-medium px-3 py-1 rounded-md transition
             bg-[#112A47] text-white
             hover:bg-[#1c3b63]"
              >
                Clear All Filters
              </button>
            )}
          </div>

          <div className="col-span-12 md:col-span-3 lg:col-span-1">
            <label className="my-2 font-semibold">From :</label>
            <input
              type="date"
              value={startDate}
              onChange={(e) => setStartDate(e.target.value)}
              className="bg-[#F3F8FB] w-full p-2 border-2 rounded border-black"
            />
          </div>

          <div className="col-span-12 md:col-span-3 lg:col-span-1">
            <label className="my-2 font-semibold">To :</label>
            <input
              type="date"
              value={endDate}
              onChange={(e) => setEndDate(e.target.value)}
              className="bg-[#F3F8FB] w-full p-2 border-2 rounded border-black"
              min={startDate}
            />
          </div>

          {/* Member Filter */}
          <div className="col-span-12 md:col-span-3 lg:col-span-2">
            <label className="my-2 font-semibold">Member Name :</label>
            <Autocomplete
              options={memberOptions}
              value={selectedMember}
              onChange={(e, val) => setSelectedMember(val || null)}
              getOptionLabel={(opt) => opt?.name || ""}
              isOptionEqualToValue={(opt, val) => opt?.id === val?.id}
              popupIcon={<FaChevronDown className="text-gray-600 text-sm" />}
              clearIcon={<FaTimes className="text-gray-600 text-xs" />}
              renderInput={(params) => (
                <TextField
                  {...params}
                  placeholder="Select Member"
                  size="small"
                  className="bg-white rounded-md custom-input"
                  InputProps={{
                    ...params.InputProps,
                    endAdornment: <>{params.InputProps.endAdornment}</>,
                  }}
                />
              )}
            />
          </div>

          {/* Company Filter */}
          <div className="col-span-12 md:col-span-3 lg:col-span-2">
            <label className="my-2 font-semibold">Company Name :</label>
            <Autocomplete
              options={companyOptions}
              value={selectedCompany}
              onChange={(e, val) => setSelectedCompany(val || null)}
              getOptionLabel={(opt) => opt?.name || ""}
              isOptionEqualToValue={(opt, val) => opt?.id === val?.id}
              popupIcon={<FaChevronDown className="text-gray-600 text-sm" />}
              clearIcon={<FaTimes className="text-gray-600 text-xs" />}
              renderInput={(params) => (
                <TextField
                  {...params}
                  placeholder="Select Company"
                  size="small"
                  className="bg-white rounded-md custom-input"
                  InputProps={{
                    ...params.InputProps,
                    endAdornment: <>{params.InputProps.endAdornment}</>,
                  }}
                />
              )}
            />
          </div>

          {/* Project Filter */}
          <div className="col-span-12 md:col-span-3 lg:col-span-2">
            <label className="my-2 font-semibold">Project Name :</label>
            <Autocomplete
              options={projectOptions}
              value={selectedProject}
              onChange={(e, val) => setSelectedProject(val || null)}
              getOptionLabel={(opt) => opt?.name || ""}
              isOptionEqualToValue={(opt, val) => opt?.id === val?.id}
              popupIcon={<FaChevronDown className="text-gray-600 text-sm" />}
              clearIcon={<FaTimes className="text-gray-600 text-xs" />}
              renderInput={(params) => (
                <TextField
                  {...params}
                  placeholder="Select Project"
                  size="small"
                  className="bg-white rounded-md custom-input"
                  InputProps={{
                    ...params.InputProps,
                    endAdornment: <>{params.InputProps.endAdornment}</>,
                  }}
                />
              )}
            />
          </div>
        </div>

        {/* GRID */}
        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 xl:grid-cols-5 gap-6">
          {filteredUsers?.map((user, index) => {
            /* 🔥 TOTAL RATING LOGIC */
            const filteredTasks = user?.RatingRecord || [];

            // COMPLETION RATE based on filtered tasks
            const totalObtainedRating = filteredTasks.reduce(
              (sum, r) => sum + Number(r?.Rating || 0),
              0,
            );
            const totalPossibleRating = filteredTasks.length * 10;
            const completionRate = totalPossibleRating
              ? Math.round((totalObtainedRating / totalPossibleRating) * 100)
              : 0;

            // COMPLETED CALLS
            const calls = filteredTasks
              .filter((r) => r?.ConplainId?.CallNo)
              .map((r) => r.ConplainId.CallNo);

            const isExpanded = expandedUsers[user._id];
            const visibleCalls = getVisibleCalls(calls, user._id);
            const hasMoreCalls = calls.length > maxCalls;

            return (
              <div
                key={user._id || index}
                className="bg-white rounded-xl shadow-md p-4 flex flex-col gap-4 hover:shadow-lg transition-shadow"
              >
                {/* HEADER */}
                <div className="flex items-center gap-3">
                  <img
                    src={
                      user.ProfilePhoto && user.ProfilePhoto !== ""
                        ? user.ProfilePhoto
                        : defaultProfile
                    }
                    alt="profile"
                    className="w-12 h-12 rounded-full object-contain"
                  />
                  <div>
                    <p className="font-semibold text-md">{user.Name}</p>
                    <p className="text-xs text-gray-500">{user.Role}</p>
                  </div>
                </div>

                {/* SCORE */}
                <div className="flex items-end gap-2">
                  <span className="text-4xl font-bold text-[#112A47]">
                    {filteredTasks.length || 0}
                  </span>
                  <span className="text-sm text-[#112A47] mb-1">tasks</span>
                </div>

                {/* COMPLETION RATE */}
                <div>
                  <div className="flex justify-between text-sm font-semibold mb-1 text-[#112A47]">
                    <span>Completion Rate</span>
                    <span>{completionRate}%</span>
                  </div>
                  <div className="w-full bg-gray-200 h-2 rounded-full">
                    <div
                      className="bg-[#122A47] h-2 rounded-full"
                      style={{ width: `${completionRate}%` }}
                    />
                  </div>
                </div>

                {/* COMPLETED CALLS */}
                <div>
                  <div className="flex items-center justify-between mb-2">
                    <p className="text-sm font-semibold text-[#112A47]">
                      Completed Calls ({calls.length})
                    </p>
                    {hasMoreCalls && (
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          toggleExpanded(user._id);
                        }}
                        className="text-xs text-[#112A47] flex items-center gap-1 hover:text-blue-800"
                      >
                        {isExpanded ? (
                          <>
                            <span>Show Less</span>
                            <FaChevronDown size={10} />
                          </>
                        ) : (
                          <>
                            <span>Show More</span>
                            <FaChevronRight size={10} />
                          </>
                        )}
                      </button>
                    )}
                  </div>

                  {!calls || calls.length === 0 ? (
                    <p className="text-sm text-red-500">
                      Completed Calls not found
                    </p>
                  ) : (
                    <div className="flex flex-wrap gap-2">
                      {visibleCalls.map((call, i) => (
                        <span
                          key={i}
                          className="inline-flex items-center text-sm px-3 py-1 bg-[#112A47]/10 rounded-full font-medium text-[#112A47]"
                        >
                          <FaHashtag className="mr-1 text-xs" />
                          {call}
                        </span>
                      ))}
                    </div>
                  )}
                </div>

                {/* FOOTER */}
                <button
                  onClick={() => {
                    setSelectedUserId(user._id);
                    setOpen(true);
                  }}
                  className="w-full All_button text-white text-sm font-medium py-1.5 rounded-md transition-all duration-300 hover:shadow active:scale-95 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-1 relative z-30 mt-auto  flex items-center justify-center gap-1"
                >
                  View Details
                  <span className="text-base leading-none">›</span>
                </button>
              </div>
            );
          })}
        </div>

        {/* NO RESULTS MESSAGE */}
        {filteredUsers.length === 0 && (
          <div className="text-center py-10">
            <p className="text-gray-500 text-lg font-semibold">
              No users found with the selected filters
            </p>
            <button
              onClick={clearAllFilters}
              className="mt-4 px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition"
            >
              Clear All Filters
            </button>
          </div>
        )}


        {/* User Details Modal */}
        {open && selectedUser && (
          <div
            className="fixed top-0 left-0 w-screen h-screen z-[9999] flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm animate-in fade-in duration-200"
            onClick={closeDrawer}
          >
            <div
              onClick={(e) => e.stopPropagation()}
              className="bg-[#F4F7FB] rounded-xl shadow-2xl w-full max-w-2xl h-[90vh] overflow-hidden flex flex-col animate-in zoom-in-95 duration-200"
            >
              {/* Header */}
              <div className="bg-[#112A47] text-white p-6 flex justify-between items-start">
                <div className="flex items-center gap-4">
                  <div className="w-16 h-16 rounded-full border-2 border-white/20 p-1 bg-white/5 shrink-0">
                    <img
                      src={selectedUser.ProfilePhoto || defaultProfile}
                      alt=""
                      className="w-full h-full rounded-full object-cover"
                      onError={(e) => { e.target.src = defaultProfile }}
                    />
                  </div>
                  <div>
                    <h3 className="text-xl font-bold leading-tight">{selectedUser.Name}</h3>
                    <p className="text-white/60 text-sm mt-1">{selectedUser.Role}</p>
                  </div>
                </div>
                <button onClick={closeDrawer} className="text-white/60 hover:text-white bg-white/10 hover:bg-white/20 p-2 rounded-full transition-colors">
                  <FaTimes size={16} />
                </button>
              </div>

              {/* Task Count Bar */}
              <div className="bg-white border-b border-gray-200 px-6 py-3 flex items-center gap-2 font-semibold text-gray-700 shadow-sm z-10">
                <FaRegCircleCheck className="text-[#112A47]" />
                <span>{selectedUser.RatingRecord?.length || 0} Completed Tasks</span>
              </div>

              {/* List */}
              <div className="p-6 overflow-y-auto custom-scrollbar space-y-4 bg-[#F4F7FB] flex-1">
                {selectedUser.RatingRecord?.map((record, index) => (
                  <div
                    key={record._id || index}
                    onClick={() => {
                      const workId = record.ConplainId?._id;
                      if (workId) {
                        window.open(`/InhouseRnd/CustomerrequustDetail/${workId}`, '_blank');
                      }
                    }}
                    className="bg-white p-5 rounded-xl border border-gray-200 shadow-sm hover:shadow-md transition-all cursor-pointer hover:border-blue-300 group"
                  >
                    <div className="flex justify-between items-start mb-4">
                      <span className="bg-[#112A47] text-white text-xs font-bold px-3 py-1.5 rounded flex items-center gap-2 group-hover:bg-blue-900 transition-colors">
                        <FaHashtag className="opacity-70" /> {record.ConplainId?.CallNo || "N/A"}
                      </span>
                      <span className="bg-gray-100 text-gray-700 text-xs font-bold px-3 py-1.5 rounded border border-gray-200">
                        Rating: {record.Rating || "-"}
                      </span>
                    </div>

                    <div className="mb-4 space-y-3">
                      <div>
                        <p className="text-[10px] text-gray-400 uppercase font-bold tracking-wider mb-1">COMPANY:</p>
                        <p className="font-bold text-gray-800 text-sm leading-tight group-hover:text-blue-800 transition-colors">
                          {record.ConplainId?.CompanyName?.WhaClientName || record.ConplainId?.CompanyName?.CompanyName || "-"}
                        </p>
                      </div>
                      <div>
                        <p className="text-[10px] text-gray-400 uppercase font-bold tracking-wider mb-1">PROJECT:</p>
                        <p className="font-bold text-gray-800 text-sm leading-tight">
                          {record.ConplainId?.Project?.project_name || "-"}
                        </p>
                      </div>
                    </div>

                    <div className="pt-3 border-t border-gray-100 flex items-center gap-2 text-xs text-gray-400 font-medium">
                      <FaHistory />
                      {moment(record.Date).format("DD MMM YYYY, hh:mm a")}
                    </div>
                  </div>
                ))}
                {(!selectedUser.RatingRecord || selectedUser.RatingRecord.length === 0) && (
                  <div className="text-center py-10 text-gray-400">
                    No completed tasks found.
                  </div>
                )}
              </div>
            </div>
          </div>
        )}
      </div>
    </>
  );
};

export default TaskReport;